/**
 * dp-header.js — Verhalten für sections/dp-header.liquid.
 *
 * Baut auf denselben theme-internen Bausteinen auf, die Tinkers eigener Header
 * benutzt (sections/header.liquid + assets/header-drawer.js), statt eigene
 * Fokusfallen- oder Scroll-Logik neu zu erfinden:
 *   - @theme/component   → Component-Basisklasse + das globale on:click/on:keydown-
 *                          Eventsystem, das theme-weit bereits läuft.
 *   - @theme/focus       → trapFocus()/removeTrapFocus(), identisch zu HeaderDrawer.
 *   - @theme/utilities    → lockScroll()/unlockScroll() (dieselbe Scroll-Sperre wie
 *                          Tinkers Such- und Warenkorb-Dialoge) und onAnimationEnd().
 *   - @theme/scroll-container → liest die Scroll-Position vom *richtigen* Container.
 *                          Ab 990px scrollt in diesem Theme `.page-wrapper`, nicht
 *                          `document` — ein einfacher `window.scroll`-Listener würde
 *                          auf Desktop schweigen.
 */

import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, unlockScroll, onAnimationEnd } from '@theme/utilities';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Mobiles Schubladenmenü.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLButtonElement} trigger - Der Hamburger-Button, öffnet/schließt.
 * @property {HTMLElement} panel - Der Schubladen-Container (Overlay + Inhalt).
 * @property {HTMLButtonElement} closeButton - Schließen-Button innerhalb des Panels.
 *
 * @extends {Component<DrawerRefs>}
 */
class DpHeaderDrawer extends Component {
  requiredRefs = ['trigger', 'panel', 'closeButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeydown);
    removeTrapFocus();
    unlockScroll(this);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;
    const { trigger, panel, closeButton } = this.refs;

    panel.hidden = false;
    lockScroll(this);

    // DOM-Schreibvorgang (hidden entfernen) und Klassenwechsel getrennt halten,
    // damit die Öffnen-Animation zuverlässig startet statt übersprungen zu werden.
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');

      onAnimationEnd(panel, () => {
        trapFocus(panel);
        closeButton.focus();
      });
    });
  }

  close() {
    if (!this.isOpen) return;
    const { trigger, panel } = this.refs;

    this.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    removeTrapFocus();
    unlockScroll(this);
    trigger.focus();

    onAnimationEnd(panel, () => {
      if (!this.isOpen) panel.hidden = true;
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    this.close();
  };
}

if (!customElements.get('dp-header-drawer')) {
  customElements.define('dp-header-drawer', DpHeaderDrawer);
}

/**
 * Sticky-Verhalten: erscheint beim Hochscrollen, verschwindet beim Runterscrollen,
 * bleibt oben auf der Seite immer sichtbar. Reine Transform-Animation (kein
 * Layout-Sprung), Höhe wird laufend als `--header-height` bereitgestellt, damit
 * andere Theme-Bausteine (z. B. Sticky-Warenkorb-Summary) korrekt andocken.
 *
 * @typedef {object} StickyRefs
 * @property {HTMLElement} bar - Die sichtbare Kopfzeile, deren Höhe gemessen wird.
 *
 * @extends {Component<StickyRefs>}
 */
class DpHeaderSticky extends Component {
  requiredRefs = ['bar'];

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {boolean} */
  #ticking = false;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /**
   * `#header-group` hält Ankündigungsband + Header zusammen. Andere Kern-Bausteine
   * (z. B. das Produktbild auf der Produktseite, `blocks/_product-details.liquid`)
   * lesen `--header-group-height`, um ihre Höhe an "Viewport minus Kopfbereich"
   * auszurichten. Tinkers eigener globaler Updater (`updateHeaderHeights()` in
   * utilities.js) sucht dafür den Tag `header-component` — den dieser Header
   * bewusst nicht verwendet (siehe Kommentar oben an der Komponente), deshalb
   * pflegen wir die Variable hier selbst nach, sonst bliebe sie bei 0 stehen.
   * @type {HTMLElement | null}
   */
  #headerGroup = null;

  connectedCallback() {
    super.connectedCallback();

    this.#headerGroup = document.getElementById('header-group');

    this.#resizeObserver.observe(this.refs.bar);
    if (this.#headerGroup) this.#resizeObserver.observe(this.#headerGroup);

    this.#attachScrollListener();
    scrollContainerMediaQuery.addEventListener('change', this.#attachScrollListener);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#attachScrollListener);
  }

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === this.refs.bar && entry.borderBoxSize[0]) {
        const height = Math.round(entry.borderBoxSize[0].blockSize);
        document.body.style.setProperty('--header-height', `${height}px`);
      } else if (entry.target === this.#headerGroup) {
        this.#updateGroupHeight();
      }
    }
  });

  #updateGroupHeight() {
    if (!this.#headerGroup) return;

    let total = 0;
    for (const element of this.#headerGroup.children) {
      if (element instanceof HTMLElement) total += element.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${total}px`);
  }

  /**
   * (Re-)bindet den Scroll-Listener an den aktuell korrekten Scroll-Container.
   * Nötig, weil sich der Container beim Wechsel über die 990px-Grenze ändert.
   */
  #attachScrollListener = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      this.#update();
      this.#ticking = false;
    });
  };

  #update() {
    const scrollTop = Math.max(0, getScrollTop());
    const delta = scrollTop - this.#lastScrollTop;
    const barHeight = this.refs.bar.offsetHeight || 0;

    this.classList.toggle('is-scrolled', scrollTop > 4);

    if (scrollTop <= barHeight) {
      // Ganz oben auf der Seite: nie verstecken, damit der erste Eindruck stimmt.
      this.classList.remove('dp-header--hidden');
    } else if (delta > 4) {
      this.classList.add('dp-header--hidden');
    } else if (delta < -4) {
      this.classList.remove('dp-header--hidden');
    }

    this.#lastScrollTop = scrollTop;
  }
}

if (!customElements.get('dp-header-sticky')) {
  customElements.define('dp-header-sticky', DpHeaderSticky);
}
